import { Spinner, Switch, CircularSlider, GhostButton } from '@mgh-app/component-library';
import { MainLayoutContext } from 'layouts/MainLayout';
import { SyntheticEvent, useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { selectRoomById } from 'services/RoomService';
import {
  useGetLightQuery,
  useGetThingInfoQuery,
  useSetLightValueMutation,
} from 'services/ThingsService';
import { useAppSelector } from 'store';
import {
  alignItems,
  borderRadius,
  boxShadow,
  display,
  flexDirection,
  fontSize,
  gap,
  gridTemplateRows,
  height,
  inset,
  justifyContent,
  justifyItems,
  margin,
  padding,
  position,
  tailwindClassNameConvertNegative,
  textColor,
  TMargin,
  TTranslate,
  twCls,
  width,
} from 'style';

import { LIGHT_DIMMER_DELAY, POLLING_INTERVAL } from 'services/apiConfig';
import { ToggleState } from 'models/Thing';
import debounce from 'lodash.debounce';

export const Light = (): JSX.Element => {
  const { setTitle, showNavigationButtons } = useContext(MainLayoutContext);
  const { lightId = '', roomId = '' } = useParams();
  const room = useAppSelector((state) => selectRoomById(state, roomId));
  // const thing = useAppSelector((state) => selectThing(state, roomId, lightId)); // TODO: thing is cleared from the store after polling
  const { data: thing } = useGetThingInfoQuery({ roomId, thingId: lightId });
  const { data: light, isLoading } = useGetLightQuery(lightId, {
    pollingInterval: POLLING_INTERVAL,
    refetchOnMountOrArgChange: true,
  });
  const [setLightValue] = useSetLightValueMutation();

  useEffect(() => {
    room?.nickName && setTitle(room?.nickName);
    showNavigationButtons(true);

    if (light) {
      setLightPower(light.power);
      setLightBrightness(light.brightness);
    }
  }, [light, room, room?.nickName, setTitle, showNavigationButtons]);

  const [lightPower, setLightPower] = useState<ToggleState>();
  const [lightBrightness, setLightBrightness] = useState<number | undefined>(undefined);

  const handleDecreaseLightBrightness = () => {
    if (light && lightPower != undefined && lightBrightness != undefined && lightBrightness > 0) {
      const newValue = Math.max(0, lightBrightness - 5);
      setLightBrightness(newValue);
      setLightValue({ ...light, power: lightPower, brightness: newValue });
    }
  };

  const handleIncreaseLightBrightness = () => {
    if (light && lightPower != undefined && lightBrightness != undefined && lightBrightness < 100) {
      const newValue = Math.min(100, lightBrightness + 5);
      setLightBrightness(newValue);
      setLightValue({ ...light, power: lightPower, brightness: newValue });
    }
  };

  const changeLightBrightness = useMemo(
    () =>
      debounce(
        (value: number) =>
          light &&
          lightPower &&
          setLightValue({
            ...light,
            power: lightPower,
            brightness: value,
          }),
        LIGHT_DIMMER_DELAY,
        { leading: false, trailing: true },
      ),
    [light, lightPower, setLightValue],
  );

  const handleLightBrightnessChange = (value: number) => {
    setLightBrightness(value);
    changeLightBrightness(value);
  };

  const handleLightSwitch = (_: SyntheticEvent, checked: boolean) => {
    setLightPower(checked ? 'ON' : 'OFF');
    if (light && lightBrightness != undefined) {
      setLightValue({
        ...light,
        power: checked ? 'ON' : 'OFF',
        brightness: lightBrightness,
      });
    }
  };

  return (
    <div
      className={twCls(
        margin('mt-6'),
        display('grid'),
        gap('gap-10'),
        gridTemplateRows('grid-rows-min-fc'),
        justifyContent('justify-center'),
        justifyItems('justify-items-center'),
      )}
    >
      <h2>{thing?.name}</h2>
      {isLoading && <Spinner />}
      {!isLoading && light && (
        <>
          <div className={twCls(position('relative'))}>
            <CircularSlider
              handle={{
                value: lightBrightness ?? 0,
                onChange: handleLightBrightnessChange,
                color: '#2396fd',
              }}
              arcColor="#2396fd"
              arcBackgroundColor="#eaeaea"
              size={320} // size = height + 2*top
              startAngle={80}
              endAngle={280}
              handleSize={10}
              coerceToInt={true}
              trackWidth={5}
            />
            <div
              className={twCls(
                position('absolute'),
                inset('top-10', 'left-1/2'), // top
                tailwindClassNameConvertNegative<TTranslate>('-translate-x-1/2'),
                display('flex'),
                flexDirection('flex-col'),
                alignItems('items-center'),
                justifyContent('justify-center'),
                width('w-60'),
                height('h-60'), // height
                borderRadius('rounded-full'),
                boxShadow('shadow-shades-outer'),
                textColor('text-blue-100'),
              )}
            >
              <div className={twCls(display('flex'), justifyContent('justify-center'))}>
                <span className={twCls(fontSize('text-4xl'))}>{lightBrightness}</span>
                <span
                  className={twCls(
                    display('flex'),
                    alignItems('items-end'),
                    fontSize('text-xl'),
                    padding('pb-0.5'),
                  )}
                >
                  %
                </span>
              </div>
            </div>
            <div
              className={twCls(
                position('absolute'),
                inset('top-48', 'left-0'),
                tailwindClassNameConvertNegative<TMargin>('-ml-3'),
              )}
            >
              <GhostButton
                className={twCls(fontSize('text-4xl'), textColor('text-grey-900'))}
                onClick={handleDecreaseLightBrightness}
              >
                -
              </GhostButton>
            </div>
            <div
              className={twCls(position('absolute'), inset('top-48', 'left-64'), margin('ml-3'))}
            >
              <GhostButton
                className={twCls(fontSize('text-4xl'), textColor('text-grey-900'))}
                onClick={handleIncreaseLightBrightness}
              >
                +
              </GhostButton>
            </div>
          </div>
          {lightPower && <Switch checked={lightPower === 'ON'} onChange={handleLightSwitch} />}
        </>
      )}
    </div>
  );
};
